import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { child, get, ref, remove, set } from 'firebase/database';

import { Message, Conversation } from '../models/chat.model';
import { AIFireService } from './fire.service';
import { database } from './fire-init';
import { getClariChatModelNameMap } from '../shared/clari-chat-model-catalog';

@Injectable({ providedIn: 'root' })
export class FlameThrowerService {
  private readonly MODEL_LABELS: Record<string, string> = {
    ...getClariChatModelNameMap(),
    gemini3flash: 'Gemini Flash 3'
  };

  private readonly ALLOWED_MODELS = new Set(Object.keys(this.MODEL_LABELS));
  private readonly BACKUP_ORDER: string[] = ['haiku45', 'sonnet45'];

  private labelFor = (id: string) => this.MODEL_LABELS[id] ?? id;

  private createAssistantMsg(
    modelId: string,
    text: string,
    image?: string | null,
    tokens?: number,
    images?: string[]
  ): Message {
    return {
      id: uuidv4(),
      role: 'assistant',
      content: text,
      timestamp: new Date(),
      modelId,
      modelDisplayName: this.labelFor(modelId),
      image: image || undefined,
      tokens,
      // @ts-ignore
      images
    };
  }

  private conversationsSubject = new BehaviorSubject<Conversation[]>([]);
  private currentConversationSubject = new BehaviorSubject<Conversation | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  conversations$ = this.conversationsSubject.asObservable();
  currentConversation$ = this.currentConversationSubject.asObservable();
  isLoading$ = this.isLoadingSubject.asObservable();

  constructor(private aiFire: AIFireService) {
    this.loadConversations();
  }

  private async loadConversations(): Promise<void> {
    try {
      const snap = await get(child(ref(database), 'conversations'));
      const convs: Conversation[] = [];

      if (snap.exists()) {
        const data = snap.val() as Record<string, any>;
        const sorted = Object.keys(data)
          .map(k => ({ k, updated: new Date(data[k].updatedAt) }))
          .sort((a, b) => b.updated.getTime() - a.updated.getTime())
          .slice(0, 10);

        for (const { k } of sorted) {
          const raw = data[k];
          convs.push({
            id: raw.id,
            title: raw.title,
            messages: (raw.messages || []).map((m: any) => ({
              ...m,
              timestamp: new Date(m.timestamp)
            })),
            createdAt: new Date(raw.createdAt),
            updatedAt: new Date(raw.updatedAt)
          });
        }
      }

      this.conversationsSubject.next(convs);
    } catch (e) {
      console.error('loadConversations error', e);
    }
  }

  private async saveConversationToDatabase(c: Conversation): Promise<void> {
    try {
      await set(ref(database, `conversations/${c.id}`), c);
    } catch (e) {
      console.error('saveConversation error', e);
    }
  }

  private updateConversationInMemory(c: Conversation): void {
    const rest = this.conversationsSubject.value.filter(v => v.id !== c.id);
    this.conversationsSubject.next([c, ...rest].slice(0, 10));
    this.currentConversationSubject.next(c);
  }

  async updateConversation(c: Conversation): Promise<void> {
    c.updatedAt = new Date();
    this.updateConversationInMemory(c);
    await this.saveConversationToDatabase(c);
  }

  async createNewConversation(): Promise<Conversation> {
    const c: Conversation = {
      id: uuidv4(),
      title: 'New Conversation',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    this.conversationsSubject.next([c, ...this.conversationsSubject.value].slice(0, 5));
    this.currentConversationSubject.next(c);
    await this.saveConversationToDatabase(c);
    return c;
  }

  selectConversation(id: string): void {
    const c = this.conversationsSubject.value.find(v => v.id === id) || null;
    this.currentConversationSubject.next(c);
  }

  sendMessage(
    content: string,
    model: string = 'maverick',
    opts?: {
      webSearch?: boolean;
      image?: File;
    }
  ): Observable<Message[]> {
    if (!content || !content.trim()) return of([]);

    let conv = this.currentConversationSubject.value;
    const normalizedModel = this.normalizeModel(model);
    const userMsg: Message = {
      id: uuidv4(),
      role: 'user',
      content,
      timestamp: new Date()
    };

    const handle = (c: Conversation): Observable<Message[]> => {
      c.messages.push(userMsg);
      if (c.messages.length === 1) {
        c.title = content.length > 30 ? content.slice(0, 30) + '…' : content;
      }

      this.isLoadingSubject.next(true);

      return from(this.updateConversation(c)).pipe(
        switchMap(() => {
          const hist = c.messages.slice(-5);
          const image = opts?.image;
          const fallbackChain = this.buildFallbackChain(normalizedModel);

          return this.requestAssistantWithFallback(content, hist, fallbackChain, image).pipe(
            map((assistantMsg: Message) => [userMsg, assistantMsg]),
            catchError(err => {
              console.error('AI error', err);
              return of([
                userMsg,
                this.createAssistantMsg(
                  fallbackChain[fallbackChain.length - 1] || normalizedModel,
                  'Sorry, I encountered an error.'
                )
              ]);
            }),
            switchMap((msgs: Message[]) => {
              msgs.slice(1).forEach(m => c.messages.push(m));
              return from(this.updateConversation(c)).pipe(map(() => msgs));
            })
          );
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
    };

    if (!conv) {
      return from(this.createNewConversation()).pipe(switchMap(c => handle(c)));
    }

    return handle(conv);
  }

  private buildFallbackChain(model: string): string[] {
    const chain = [model, ...this.BACKUP_ORDER]
      .map(m => this.normalizeModel(m))
      .filter(m => this.ALLOWED_MODELS.has(m));

    return Array.from(new Set(chain));
  }

  private normalizeModel(model: string): string {
    const raw = String(model || '').trim().toLowerCase();

    const aliases: Record<string, string> = {
      llama4: 'maverick',
      llama: 'maverick',
      palmyra: 'palmx',
      geminiflash3: 'gemini3flash',
      'gemini-flash-3': 'gemini3flash',
      deepseekv3: 'deepv3',
      'deepseek-v3': 'deepv3',
      pixtral: 'pixtralimage',
      haiku: 'haiku45',
      sonnet: 'sonnet45',
      chatgpt: 'chatgpt54',
      chatgpt5: 'chatgpt5',
      chatgpt52: 'chatgpt5',
      kimi25: 'kimik25',
      'kimi-2.5': 'kimik25',
      'kimi2.5': 'kimik25',
      moonshotkimi: 'kimik25'
    };

    const normalized = aliases[raw] || raw;
    return this.ALLOWED_MODELS.has(normalized) ? normalized : 'maverick';
  }

  private requestAssistantWithFallback(
    content: string,
    history: Message[],
    chain: string[],
    image?: File,
    index = 0
  ): Observable<Message> {
    const modelId = chain[index] || 'haiku45';

    return this.requestAssistantMessage(modelId, content, history, image).pipe(
      catchError(err => {
        console.error(`[FlameThrowerService] ${modelId} failed`, err);
        if (index < chain.length - 1) {
          return this.requestAssistantWithFallback(content, history, chain, image, index + 1);
        }
        throw err;
      })
    );
  }

  private requestAssistantMessage(
    modelId: string,
    content: string,
    history: Message[],
    image?: File
  ): Observable<Message> {
    const f = this.aiFire;

    switch (modelId) {
      case 'maverick':
      case 'llama31Instruct':
        return f.generateMaverickText(content, history).pipe(
          map((text) => this.createAssistantMsg(modelId, text))
        );

      case 'opus46':
      case 'opus6':
        return f.generateOpyDopy6(content, history).pipe(
          map((text) => this.createAssistantMsg('opus46', text))
        );

      case 'opus47':
        return f.generateOpus47Text(content, history).pipe(
          map((text) => this.createAssistantMsg('opus47', text))
        );

      case 'kimik25':
        return f.generateKimiK25Text(content, history).pipe(
          map((text) => this.createAssistantMsg('kimik25', text))
        );

      case 'opus45':
        return f.generateOpus45Text(content, history).pipe(
          map((text) => this.createAssistantMsg('opus45', text))
        );

      case 'haiku45':
        return f.generateHaiku45Text(content, history).pipe(
          map((text) => this.createAssistantMsg('haiku45', text))
        );

      case 'sonnet45':
        return f.generateSonnet45Text(content, history).pipe(
          map((text) => this.createAssistantMsg('sonnet45', text))
        );

      case 'palmx':
        return f.generatePalmXText(content, history).pipe(
          map((text) => this.createAssistantMsg('palmx', text))
        );

      case 'cohere':
      case 'commandRInstruct':
        return f.generateCohereText(content, history).pipe(
          map((text) => this.createAssistantMsg(modelId, text))
        );

      case 'commandLight':
        return f.generateCommandLightText(content, history).pipe(
          map((text) => this.createAssistantMsg('commandLight', text))
        );

      case 'gemini':
      case 'gemini3flash':
        return f.generateGeminiText(content, history, image).pipe(
          map((text) => this.createAssistantMsg(modelId, String(text ?? '')))
        );

      case 'deepv3':
        return f.generateDeepSeekV3Text(content, history).pipe(
          map((text) => this.createAssistantMsg('deepv3', text))
        );

      case 'deepseek':
        return f.generateDeepSeekText(content, history).pipe(
          map((text) => this.createAssistantMsg('deepseek', text))
        );

      case 'mistrallarge':
        return f.generateMistralLargeText(content, history).pipe(
          map((text) => this.createAssistantMsg('mistrallarge', text))
        );

      case 'codestralto':
        return f.generateCodestraltoText(content, history).pipe(
          map((text) => this.createAssistantMsg('codestralto', text))
        );

      case 'pixtralimage':
        return f.generatePixtralImageText(content, history, image).pipe(
          map((resp: { text: string; image?: string }) =>
            this.createAssistantMsg('pixtralimage', resp.text, resp.image ?? null)
          )
        );

      case 'chatgpt54':
        return f.generateChatGPT54Text(content, history, image).pipe(
          map((text) => this.createAssistantMsg('chatgpt54', text))
        );

      case 'chatgpt5':
        return f.generateChatGPT54Text(content, history, image).pipe(
          map((text) => this.createAssistantMsg('chatgpt5', text))
        );

      case 'minichatgpt54':
        return f.generateMiniChatGPT54Text(content, history).pipe(
          map((text) => this.createAssistantMsg('minichatgpt54', text))
        );

      case 'scout':
      case 'novalite':
        return f.generateScoutText(content, history).pipe(
          map((text) => this.createAssistantMsg(modelId, String(text ?? '')))
        );

      case 'jamba':
        return f.generateJambaText(content, history).pipe(
          map((text) => this.createAssistantMsg('jamba', text))
        );

      case 'premier':
        return f.generateNovaProText(content, history).pipe(
          map((resp: any) => this.createAssistantMsg('premier', String(resp?.text ?? '')))
        );

      case 'novapro':
        return f.generateNovaProText(content, history).pipe(
          map((r: { text: string }) => this.createAssistantMsg('novapro', String(r?.text ?? '')))
        );

      default:
        return f.generateHaiku45Text(content, history).pipe(
          map((text) => this.createAssistantMsg(modelId, text))
        );
    }
  }

  async deleteConversation(id: string): Promise<void> {
    this.conversationsSubject.next(this.conversationsSubject.value.filter(c => c.id !== id));
    if (this.currentConversationSubject.value?.id === id) {
      this.currentConversationSubject.next(null);
    }
    try {
      await remove(ref(database, `conversations/${id}`));
    } catch (e) {
      console.error('deleteConversation error', e);
    }
  }

  async clearAllConversations(): Promise<void> {
    this.conversationsSubject.next([]);
    this.currentConversationSubject.next(null);
  }
}
